import React from 'react';
import {
  BarChart3, Zap, Shield, TrendingUp, CheckCircle, Star,
  ArrowRight, Clock, Award, Sparkles, Target, Brain,
  Rocket, Mail, DollarSign
} from 'lucide-react';
import { Auth } from './Auth';

interface PricingPlan {
  name: string;
  price: string;
  surveys: number;
  features: string[];
  popular?: boolean;
  icon: React.ReactNode;
}

export function LandingPage() {
  const [showAuth, setShowAuth] = React.useState(false);

  if (showAuth) return <Auth />;

  const plans: PricingPlan[] = [
    {
      name: 'البداية المجانية', price: 'مجاناً', surveys: 3,
      features: ['3 استبانات مجانية','تحليل إحصائي كامل','رسوم بيانية احترافية','تصدير Excel متقدم','دعم فني أساسي'],
      icon: <Sparkles style={{ width: 20, height: 20 }} />,
    },
    {
      name: 'الباقة الاحترافية', price: '4 دنانير', surveys: 10, popular: true,
      features: ['حتى 10 استبانات','تحليل متعدد المقاييس','انحراف معياري متقدم','تقارير مفصلة PDF','دعم فني ذهبي','أولوية في المعالجة'],
      icon: <Award style={{ width: 20, height: 20 }} />,
    },
    {
      name: 'باقة المؤسسات', price: '7 دنانير', surveys: 25,
      features: ['حتى 25 استبانة','تحليلات متقدمة AI','تقارير بحثية كاملة','مقارنات متعددة','دعم فني VIP','استشارة مجانية','تخصيص كامل'],
      icon: <Rocket style={{ width: 20, height: 20 }} />,
    },
  ];

  const features = [
    { icon: <Zap style={{ width: 22, height: 22 }} />, title: 'سرعة فائقة', desc: 'تحليل آلاف الاستجابات في ثوانٍ معدودة' },
    { icon: <Brain style={{ width: 22, height: 22 }} />, title: 'ذكاء اصطناعي', desc: 'كشف تلقائي للأنماط والمقاييس المتعددة' },
    { icon: <Shield style={{ width: 22, height: 22 }} />, title: 'أمان مطلق', desc: 'بياناتك محمية بأعلى معايير الأمان' },
    { icon: <Target style={{ width: 22, height: 22 }} />, title: 'دقة عالية', desc: 'نتائج إحصائية دقيقة 100% معتمدة أكاديمياً' },
    { icon: <TrendingUp style={{ width: 22, height: 22 }} />, title: 'رسوم متحركة', desc: 'تصورات بيانية تفاعلية وجذابة' },
    { icon: <Clock style={{ width: 22, height: 22 }} />, title: 'سهل الاستخدام', desc: 'واجهة بديهية لا تحتاج خبرة سابقة' },
  ];

  const comparisons = [
    { feature: 'السعر', ours: 'من 0 إلى 7 دنانير', others: 'من 500 إلى 2000$' },
    { feature: 'سهولة الاستخدام', ours: 'بديهي تماماً', others: 'يحتاج تدريب' },
    { feature: 'الوقت المطلوب', ours: 'دقائق', others: 'ساعات' },
    { feature: 'اللغة العربية', ours: 'دعم كامل', others: 'محدود' },
    { feature: 'التحديثات', ours: 'مجانية دائماً', others: 'مدفوعة' },
    { feature: 'الدعم الفني', ours: '24/7', others: 'محدود' },
  ];

  const stats = [
    { value: '10,000+', label: 'استبانة تم تحليلها' },
    { value: '500+', label: 'باحث راضٍ' },
    { value: '99.9%', label: 'دقة النتائج' },
    { value: '< 1 دقيقة', label: 'متوسط وقت التحليل' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', overflowX: 'hidden' }} dir="rtl">

      {/* Ambient orbs */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div className="orb orb-primary" style={{ width: 600, height: 600, top: -200, right: -150, opacity: 0.14, position: 'absolute' }} />
        <div className="orb orb-accent"  style={{ width: 500, height: 500, bottom: -100, left: -100, opacity: 0.11, position: 'absolute' }} />
      </div>

      {/* ── Navbar ── */}
      <nav style={{
        position: 'fixed', top: 0, right: 0, left: 0, zIndex: 100,
        padding: '16px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'rgba(7,11,20,0.8)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
          }}>
            <BarChart3 style={{ width: 20, height: 20, color: 'white' }} />
          </div>
          <span style={{ fontSize: 15, fontWeight: 700 }}>محلل الاستبانات الذكي</span>
        </div>
        <button onClick={() => setShowAuth(true)} className="btn-primary" style={{ fontSize: 13, padding: '8px 20px' }}>
          تسجيل الدخول
        </button>
      </nav>

      {/* ── Hero ── */}
      <section style={{ position: 'relative', zIndex: 1, padding: '140px 24px 100px', textAlign: 'center' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px',
          borderRadius: 99, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', marginBottom: 32,
        }}>
          <Sparkles style={{ width: 14, height: 14, color: '#818CF8' }} />
          <span style={{ fontSize: 13, color: '#818CF8', fontWeight: 600 }}>نظام تحليل إحصائي متقدم · مركز التعلم الإلكتروني</span>
        </div>

        <h1 style={{ fontSize: 'clamp(32px,5vw,64px)', fontWeight: 800, lineHeight: 1.2, margin: '0 auto 24px', maxWidth: 800 }}>
          <span className="gradient-text">حلل استبانات ورش العمل</span><br />
          <span>في دقائق بدلاً من أيام</span>
        </h1>

        <p style={{ fontSize: 17, color: 'var(--text-secondary)', maxWidth: 540, margin: '0 auto 40px', lineHeight: 1.8 }}>
          منصة تحليل إحصائي متكاملة مبنية لأكاديميين وباحثين الجامعات. دقة عالية، نتائج فورية، تقارير احترافية.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 40 }}>
          {[1,2,3,4,5].map(s => <Star key={s} style={{ width: 18, height: 18, fill: '#F59E0B', color: '#F59E0B' }} />)}
          <span style={{ fontSize: 13, color: 'var(--text-muted)', marginRight: 8 }}>تقييم 5.0 من 500+ باحث</span>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 72 }}>
          <button onClick={() => setShowAuth(true)} className="btn-primary"
            style={{ fontSize: 16, padding: '14px 36px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles style={{ width: 18, height: 18 }} />ابدأ مجاناً الآن<ArrowRight style={{ width: 18, height: 18 }} />
          </button>
          <a href="#pricing" className="btn-ghost"
            style={{ fontSize: 15, padding: '14px 28px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            <DollarSign style={{ width: 16, height: 16 }} />الباقات والأسعار
          </a>
        </div>

        {/* Dashboard preview mockup */}
        <div style={{ maxWidth: 760, margin: '0 auto', position: 'relative' }}>
          <div style={{
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 20, padding: 16,
            boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 60px rgba(99,102,241,0.08)',
          }}>
            <div style={{
              background: 'var(--bg-elevated)', borderRadius: 12,
              aspectRatio: '16/7', display: 'flex', alignItems: 'center',
              padding: '20px 24px', gap: 20, overflow: 'hidden',
            }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ height: 8, background: 'linear-gradient(90deg,#6366F1,#8B5CF6)', borderRadius: 99, width: '80%' }} />
                {[60,45,70].map((w,i) => <div key={i} style={{ height: 5, background: 'rgba(255,255,255,0.07)', borderRadius: 99, width: `${w}%` }} />)}
              </div>
              <div style={{
                width: 110, height: 80, background: 'rgba(99,102,241,0.08)',
                border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10,
                display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', padding: 8,
              }}>
                {[60,85,45,90,70].map((h,i) => (
                  <div key={i} style={{
                    width: 10, borderRadius: 3, height: `${h}%`,
                    background: `linear-gradient(180deg,${['#6366F1','#8B5CF6','#10B981','#6366F1','#F59E0B'][i]},transparent)`,
                  }} />
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[['4.32','#818CF8'],['3.87','#34D399'],['4.10','#FCD34D']].map(([v,c],i) => (
                  <div key={i} style={{
                    padding: '5px 10px', borderRadius: 7,
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                    fontSize: 13, fontWeight: 700, color: c as string,
                  }}>
                    {v} <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)' }}>/5</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{
            position: 'absolute', bottom: -40, left: '50%', transform: 'translateX(-50%)',
            width: '60%', height: 50, background: 'rgba(99,102,241,0.2)', filter: 'blur(30px)', pointerEvents: 'none',
          }} />
        </div>
      </section>

      {/* ── Stats ── */}
      <section style={{ position: 'relative', zIndex: 1, padding: '60px 48px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 24 }}>
          {stats.map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div className="gradient-text" style={{ fontSize: 34, fontWeight: 800, marginBottom: 6 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ position: 'relative', zIndex: 1, padding: '80px 48px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="section-label" style={{ marginBottom: 10 }}>المزايا</div>
            <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 10 }}>لماذا نحن الأفضل؟</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>مزايا لا تجدها في أي برنامج آخر</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
            {features.map((f, i) => (
              <div key={i} className="card card-glow" style={{ padding: '24px 20px' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, marginBottom: 14,
                  background: 'linear-gradient(135deg,rgba(99,102,241,0.25),rgba(139,92,246,0.15))',
                  border: '1px solid rgba(99,102,241,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#818CF8',
                }}>{f.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{f.title}</h3>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comparison ── */}
      <section style={{
        position: 'relative', zIndex: 1, padding: '80px 48px',
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}>
        <div style={{ maxWidth: 840, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div className="section-label" style={{ marginBottom: 10 }}>المقارنة</div>
            <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 10 }}>مقارنة مع البرامج التقليدية</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>لماذا تدفع آلاف الدولارات لـ SPSS وأمثاله؟</p>
          </div>
          <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              background: 'linear-gradient(135deg,rgba(99,102,241,0.2),rgba(139,92,246,0.15))',
              padding: '13px 20px', fontWeight: 700, fontSize: 13, textAlign: 'center',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
            }}>
              <div>المزية</div>
              <div style={{ color: '#818CF8' }}>محللنا الذكي ✨</div>
              <div>SPSS وأمثاله</div>
            </div>
            {comparisons.map((item, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                padding: '11px 20px', textAlign: 'center', fontSize: 13,
                background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                borderBottom: i < comparisons.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                alignItems: 'center',
              }}>
                <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{item.feature}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#34D399', fontWeight: 600 }}>
                  <CheckCircle style={{ width: 13, height: 13 }} />{item.ours}
                </div>
                <div style={{ color: '#FCA5A5' }}>{item.others}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" style={{ position: 'relative', zIndex: 1, padding: '80px 48px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="section-label" style={{ marginBottom: 10 }}>الأسعار</div>
            <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 10 }}>باقات بأسعار تنافسية</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>اختر الباقة المناسبة لاحتياجاتك</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
            {plans.map((plan, i) => (
              <div key={i} style={{
                position: 'relative', borderRadius: 18, overflow: 'hidden',
                border: plan.popular ? '1px solid rgba(99,102,241,0.5)' : '1px solid rgba(255,255,255,0.08)',
                background: plan.popular ? 'rgba(99,102,241,0.08)' : 'var(--bg-elevated)',
                boxShadow: plan.popular ? '0 0 40px rgba(99,102,241,0.2)' : 'var(--shadow-card)',
                transform: plan.popular ? 'scale(1.03)' : undefined,
              }}>
                {plan.popular && (
                  <div style={{
                    background: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
                    textAlign: 'center', padding: '6px', fontSize: 12, fontWeight: 700, color: 'white',
                  }}>⭐ الأكثر شعبية</div>
                )}
                <div style={{ padding: '24px 20px' }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, marginBottom: 14,
                    background: 'linear-gradient(135deg,rgba(99,102,241,0.3),rgba(139,92,246,0.2))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#818CF8',
                  }}>{plan.icon}</div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{plan.name}</h3>
                  <div style={{ marginBottom: 14 }}>
                    <span className="gradient-text" style={{ fontSize: 32, fontWeight: 800 }}>{plan.price}</span>
                  </div>
                  <div style={{
                    marginBottom: 18, paddingBottom: 18,
                    borderBottom: '1px solid rgba(255,255,255,0.07)', fontSize: 13, color: 'var(--text-secondary)',
                  }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color: '#818CF8' }}>{plan.surveys}</span> استبانة
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {plan.features.map((feat, fi) => (
                      <li key={fi} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12 }}>
                        <CheckCircle style={{ width: 13, height: 13, color: '#34D399', flexShrink: 0, marginTop: 2 }} />
                        <span style={{ color: 'var(--text-secondary)' }}>{feat}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => setShowAuth(true)} className="btn-primary"
                    style={{ width: '100%', justifyContent: 'center', display: 'flex' }}>
                    {i === 0 ? 'ابدأ مجاناً' : 'اشترك الآن'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section style={{ position: 'relative', zIndex: 1, padding: '72px 48px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            width: 60, height: 60, borderRadius: 18, margin: '0 auto 20px',
            background: 'linear-gradient(135deg,rgba(99,102,241,0.2),rgba(139,92,246,0.15))',
            border: '1px solid rgba(99,102,241,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Mail style={{ width: 26, height: 26, color: '#818CF8' }} />
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 10 }}>هل تريد حساباً تجريبياً؟</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 32 }}>
            راسلنا للحصول على حساب تجريبي مجاني وتجربة جميع المزايا!
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {['altiti@altiti.com', 'osamaaltiti@gmail.com'].map(email => (
              <a key={email} href={`mailto:${email}`} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                padding: '13px 20px', borderRadius: 10,
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14, transition: 'all 0.18s',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.background = 'rgba(99,102,241,0.1)';
                el.style.borderColor = 'rgba(99,102,241,0.3)';
                el.style.color = '#818CF8';
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.background = 'rgba(255,255,255,0.04)';
                el.style.borderColor = 'rgba(255,255,255,0.08)';
                el.style.color = 'var(--text-secondary)';
              }}
              >
                <Mail style={{ width: 15, height: 15 }} />{email}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{ position: 'relative', zIndex: 1, padding: '72px 24px 100px', textAlign: 'center' }}>
        <div style={{ maxWidth: 580, margin: '0 auto' }}>
          <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 14 }}>جاهز للبدء؟</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 15, marginBottom: 36 }}>
            انضم إلى مئات الباحثين الذين وفروا وقتهم ومالهم
          </p>
          <button onClick={() => setShowAuth(true)} className="btn-primary"
            style={{ fontSize: 16, padding: '15px 44px', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <Rocket style={{ width: 18, height: 18 }} />
            ابدأ التحليل الآن مجاناً
            <ArrowRight style={{ width: 18, height: 18 }} />
          </button>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 14 }}>
            لا حاجة لبطاقة ائتمان · تفعيل فوري · دعم فني 24/7
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{
        position: 'relative', zIndex: 1,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '20px 48px', textAlign: 'center', fontSize: 11, color: 'var(--text-muted)',
      }}>
        تم التطوير بواسطة أسامة صبري الطيطي · مركز التعلم الإلكتروني · جامعة الإسراء
      </footer>
    </div>
  );
}
